
import React, { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// ⚙️ FEATURE FLAG: Set to true to enable sign-ups, false to disable
const SIGNUPS_ENABLED = true;

const registerSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export type RegisterFormValues = z.infer<typeof registerSchema>;

interface RegisterFormProps {
  onSwitchForm: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchForm }) => {
  const [loading, setLoading] = useState(false);

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleRegister = async (values: RegisterFormValues) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          emailRedirectTo: window.location.origin,
          data: {
            email: values.email
          }
        }
      });

      if (error) {
        toast.error("Registration failed", {
          description: "Please check your information and try again.",
          businessName: "Authentication"
        });
        return;
      }

      // Always show success message to prevent user enumeration
      // Supabase will handle duplicate email cases silently

      toast.success("Registration successful!", {
        description: "Please check your email to verify your account.",
        businessName: "Authentication"
      });
      onSwitchForm();
      form.reset();
    } catch (error: unknown) {
      console.error("Registration error:", error);
      toast.error("An unexpected error occurred", { businessName: "Authentication" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!SIGNUPS_ENABLED && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Sign-ups Currently Unavailable</AlertTitle>
          <AlertDescription>
            We're at full capacity at the moment. Please check back later or contact support for more information.
          </AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleRegister)} className="space-y-6">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    placeholder="your@email.com"
                    {...field}
                    disabled={!SIGNUPS_ENABLED}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder="******"
                    {...field}
                    disabled={!SIGNUPS_ENABLED}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />


          <Button
            type="submit"
            className="w-full"
            disabled={loading || !SIGNUPS_ENABLED}
          >
            {loading ? "Signing up..." : !SIGNUPS_ENABLED ? "Sign-ups Disabled" : "Sign up"}
          </Button>
        </form>
      </Form>

      <div className="text-center mt-4">
        <button
          type="button"
          onClick={onSwitchForm}
          className="text-primary hover:text-primary/80 font-medium dark:text-primary dark:hover:text-primary/80"
        >
          Already have an account? Sign in
        </button>
      </div>
    </>
  );
};

export default RegisterForm;
